import type { CampaignMember, StatusMapping } from './types';
import type { SenderValue, Channel } from '../copy-tokens';
import {
  CM_STATUS_VALUES,
  SALES_TIER_VALUES,
  PRODUCT_TIER_VALUES,
  PLAN_INTERVAL_VALUES,
  PARTNER_TYPE_VALUES,
} from '../../_shared/campaign-member-picklists';

// Sourced from `../copy-tokens.ts` — the Launcher's OWN copy of the channel
// vocabulary, sender tokens, merge-token fields and template interpolation,
// frozen at pre-PR-#1465 behavior so Playbooks' resolver in
// `_shared/launcher-copy-tokens.ts` cannot change Launcher sends or previews.
// Re-exported here so v1 call sites keep their import paths.
export {
  SENDER_TOKENS,
  CHANNELS,
  COPY_VARIABLE_FIELDS,
  resolveSenderEmail,
  resolveSenderFirstName,
  resolveSenderMeetingLink,
  buildTemplateVars,
  interpolateTemplate,
  interpolateTemplateSegments,
  checkCopyVarGaps,
} from '../copy-tokens';
export type { SenderValue, Channel, CopyVariableField, TemplateSegment } from '../copy-tokens';

// The row/cell/condition shapes are inferred from the wire schemas (type-only
// imports, erased) so the type a route is parsed into IS the type the UI edits.
import type {
  ConditionOperator,
  Condition,
  ConditionGroup,
  CopyStatus,
  EmailType,
  CellConfig,
  WaterfallRow,
} from '../wire-schemas';
export type { ConditionOperator, Condition, ConditionGroup, CopyStatus, EmailType, CellConfig, WaterfallRow };

// ── Field definitions ────────────────────────────────────────────────────────

export type FieldType = 'lookup' | 'picklist' | 'boolean' | 'text' | 'number' | 'date';

export interface ConditionField {
  label: string;
  field: string; // maps to CampaignMember key
  object: string;
  type: FieldType;
  values?: string[];
}

export const CONDITION_FIELDS: ConditionField[] = [
  // Contact core
  { label: 'Contact Name',         field: 'contact_name',          object: 'Contact', type: 'text' },
  { label: 'Contact First Name',   field: 'contact_first_name',    object: 'Contact', type: 'text' },
  { label: 'Contact Last Name',    field: 'contact_last_name',     object: 'Contact', type: 'text' },
  { label: 'Contact Email',        field: 'contact_email',         object: 'Contact', type: 'text' },
  { label: 'Contact Title',        field: 'contact_title',         object: 'Contact', type: 'text' },
  { label: 'Mailing City',         field: 'mailing_city',          object: 'Contact', type: 'text' },
  { label: 'Mailing State',        field: 'mailing_state',         object: 'Contact', type: 'text' },
  // Contact custom
  { label: 'Contact Stage',        field: 'contact_stage',         object: 'Contact', type: 'text' },
  { label: 'Persona',              field: 'persona',               object: 'Contact', type: 'text' },
  { label: 'Job Level',            field: 'job_level',             object: 'Contact', type: 'text' },
  { label: 'Fit Score',            field: 'fit_score',             object: 'Contact', type: 'number' },
  { label: 'Suppressed',           field: 'suppressed',            object: 'Contact', type: 'boolean' },
  { label: 'No Longer at Company', field: 'no_longer_at_company',  object: 'Contact', type: 'boolean' },
  { label: 'Suppression Reasons',  field: 'suppression_reasons',   object: 'Contact', type: 'text' },
  { label: 'CM Status',            field: 'cm_status',             object: 'Contact', type: 'picklist', values: [...CM_STATUS_VALUES] },
  { label: 'Has Responded',        field: 'has_responded',         object: 'Contact', type: 'boolean' },
  // GTME owner
  { label: 'GTME Owner',           field: 'gtme_owner_first_name', object: 'Account', type: 'lookup' },
  { label: 'GTME Owner Email',     field: 'gtme_owner_email',      object: 'Account', type: 'text' },
  { label: 'GTME Meeting Link',    field: 'gtme_meeting_link',     object: 'Account', type: 'text' },
  { label: 'Last GTME Meeting Date', field: 'last_gtme_meeting_date', object: 'Contact', type: 'date' },
  // Growth Strategist
  { label: 'Growth Strategist',    field: 'gs_owner_first_name',   object: 'Account', type: 'lookup' },
  { label: 'GS Owner Email',       field: 'gs_owner_email',        object: 'Account', type: 'text' },
  { label: 'GS Meeting Link',      field: 'gs_meeting_link',       object: 'Account', type: 'text' },
  { label: 'Last GS Meeting Date', field: 'last_gs_meeting_date',  object: 'Contact', type: 'date' },
  // ClayDR
  { label: 'ClayDR',               field: 'claydr_first_name',     object: 'Account', type: 'lookup' },
  { label: 'ClayDR Email',         field: 'claydr_email',          object: 'Account', type: 'text' },
  { label: 'ClayDR Meeting Link',  field: 'claydr_meeting_link',   object: 'Account', type: 'text' },
  { label: 'Last ClayDR Meeting Date', field: 'last_claydr_meeting_date', object: 'Contact', type: 'date' },
  // Scanner (primary met with)
  { label: 'Scanner Name',          field: 'primary_met_with_name',  object: 'Account', type: 'lookup' },
  { label: 'Scanner Email',         field: 'primary_met_with_email', object: 'Account', type: 'text' },
  // Account-level meeting dates
  { label: 'Account Last GTME Meeting',   field: 'account_last_gtme_meeting_date',   object: 'Account', type: 'date' },
  { label: 'Account Last GS Meeting',     field: 'account_last_gs_meeting_date',     object: 'Account', type: 'date' },
  { label: 'Account Last ClayDR Meeting', field: 'account_last_claydr_meeting_date', object: 'Account', type: 'date' },
  // Account
  { label: 'Account Name',         field: 'account_name',          object: 'Account', type: 'text' },
  { label: 'Sales Tier',           field: 'sales_tier',            object: 'Account', type: 'picklist', values: [...SALES_TIER_VALUES] },
  { label: 'Account Fit',          field: 'account_fit',           object: 'Account', type: 'text' },
  { label: 'Named Account Status', field: 'named_account_status',  object: 'Account', type: 'text' },
  { label: 'Potential Contract Value', field: 'potential_contract_value', object: 'Account', type: 'number' },
  { label: 'ARR Active Subscriptions', field: 'arr_active_subscriptions', object: 'Account', type: 'number' },
  { label: 'Account Suppressed',   field: 'account_suppressed',    object: 'Account', type: 'boolean' },
  { label: 'Account Suppression Reasons', field: 'account_suppression_reasons', object: 'Account', type: 'text' },
  { label: 'Competitor',           field: 'competitor',            object: 'Account', type: 'boolean' },
  { label: 'Agency',               field: 'agency',                object: 'Account', type: 'boolean' },
  { label: 'Account Type',         field: 'account_type',          object: 'Account', type: 'text' },
  { label: 'Product Tier',         field: 'product_tier',          object: 'Account', type: 'picklist', values: [...PRODUCT_TIER_VALUES] },
  { label: 'Plan Interval',        field: 'plan_interval',         object: 'Account', type: 'picklist', values: [...PLAN_INTERVAL_VALUES] },
  { label: 'Number of Open Opps',  field: 'number_of_open_opps',   object: 'Account', type: 'number' },
  { label: 'Last Opportunity Stage', field: 'last_opportunity_stage', object: 'Account', type: 'text' },
  { label: 'Partner Type',          field: 'partner_type',           object: 'Account', type: 'picklist', values: [...PARTNER_TYPE_VALUES] },
  { label: 'Open Pipeline',         field: 'open_pipeline',          object: 'Account', type: 'number' },
  { label: 'Company Segment',      field: 'company_segment',        object: 'Account', type: 'text' },
  { label: 'Persona Tier',         field: 'persona_tier',           object: 'Contact', type: 'text' },
  { label: 'Is Requalified',       field: 'is_requalified',         object: 'Contact', type: 'boolean' },
  // Contact qualification
  { label: 'Qualification Status', field: 'qualification_status',   object: 'Contact', type: 'text' },
  // Contact dates
  { label: 'Last GTME Email Date',                  field: 'last_gtme_email_date',                 object: 'Contact', type: 'date' },
  { label: 'Last GS Email Date',                    field: 'last_gs_email_date',                   object: 'Contact', type: 'date' },
  { label: 'Last Inbound Email Date',              field: 'last_inbound_email_date',              object: 'Contact', type: 'date' },
  { label: 'Last Inbound Form Submission Date',    field: 'last_inbound_form_submission_date',    object: 'Contact', type: 'date' },
  { label: 'Last Contact Us Form Submission Date', field: 'last_contact_us_form_submission_date', object: 'Contact', type: 'date' },
  { label: 'Last ClayDR Email Date',               field: 'last_claydr_email_date',               object: 'Contact', type: 'date' },
  { label: 'Last Follow-Up Date',                  field: 'last_follow_up_date',                  object: 'Contact', type: 'date' },
  // Contact opt-out
  { label: 'Has Opted Out',        field: 'has_opted_out_of_email', object: 'Contact', type: 'boolean' },
  { label: 'Do Not Call',          field: 'do_not_call',           object: 'Contact', type: 'boolean' },
  // Legacy
  { label: 'Owner Role',           field: 'owner_role',            object: 'Contact', type: 'text' },
];

export const OPERATORS_BY_TYPE: Record<FieldType, { value: ConditionOperator; label: string }[]> = {
  lookup:   [{ value: 'is_not_null', label: '≠ null' }, { value: 'is_null', label: '= null' }, { value: 'in_any_of', label: 'is any of' }, { value: 'not_in_any_of', label: 'is not any of' }, { value: 'equals', label: '=' }, { value: 'not_equals', label: '≠' }, { value: 'contains', label: 'contains' }],
  boolean:  [{ value: 'equals', label: 'is true' }, { value: 'not_equals', label: 'is false' }],
  picklist: [{ value: 'equals', label: '=' }, { value: 'not_equals', label: '≠' }, { value: 'in_any_of', label: 'is any of' }, { value: 'not_in_any_of', label: 'is not any of' }, { value: 'contains', label: 'contains' }, { value: 'contains_any', label: 'contains any of' }, { value: 'is_null', label: '= null' }, { value: 'is_not_null', label: '≠ null' }],
  text:     [{ value: 'equals', label: '=' }, { value: 'not_equals', label: '≠' }, { value: 'in_any_of', label: 'is any of' }, { value: 'not_in_any_of', label: 'is not any of' }, { value: 'contains', label: 'contains' }, { value: 'contains_any', label: 'contains any of' }, { value: 'not_contains', label: 'not contains' }, { value: 'is_null', label: '= null' }, { value: 'is_not_null', label: '≠ null' }],
  number:   [{ value: 'equals', label: '=' }, { value: 'not_equals', label: '≠' }, { value: 'gt', label: '>' }, { value: 'lt', label: '<' }, { value: 'is_null', label: '= null' }, { value: 'is_not_null', label: '≠ null' }],
  date: [
    { value: 'in_last_n_days',     label: 'in last N days' },
    { value: 'not_in_last_n_days', label: 'not in last N days' },
    { value: 'in_next_n_days',     label: 'in next N days' },
    { value: 'not_in_next_n_days', label: 'not in next N days' },
    { value: 'equals',             label: '= (exact date)' },
    { value: 'is_null',            label: '= null' },
    { value: 'is_not_null',        label: '≠ null' },
  ],
};

export interface CopyEditorTarget {
  bucketName: string;
  statusColumn: string;
  subject: string;
  body: string;
  flowId: string;
  channel: Channel;
  copyStatus: CopyStatus | null;
  emailType: EmailType;
  sampleMember?: CampaignMember | null;
  campaign?: { id?: string; name?: string; event_date?: string | null; start_date?: string | null; type?: string; description?: string | null; invite_subject?: string | null; invite_body?: string | null } | null;
  sender?: SenderValue | null;
  matchedMembers?: CampaignMember[];
  onSave: (subject: string, body: string, status: CopyStatus, channel: Channel, emailType: EmailType) => void;
}

// ── Condition summary ─────────────────────────────────────────────────────────

export function conditionSummary(conditions: Condition[], logic: 'AND' | 'OR'): string {
  if (conditions.length === 0) return 'catch-all';
  return conditions.map(c => {
    const f = CONDITION_FIELDS.find(f => f.field === c.field);
    const label = f?.label ?? c.field;
    if (c.operator === 'in_any_of') {
      const list = Array.isArray(c.value) ? c.value : [];
      return `${label} is any of (${list.length})`;
    }
    if (c.operator === 'not_in_any_of') {
      const list = Array.isArray(c.value) ? c.value : [];
      return `${label} is not any of (${list.length})`;
    }
    if (c.operator === 'contains_any') {
      const vals = Array.isArray(c.value) ? c.value : [];
      return `${label} contains any of (${vals.join(', ')})`;
    }
    const isBool = f?.type === 'boolean';
    const op = c.operator === 'is_not_null' ? '≠ null'
      : c.operator === 'is_null' ? '= null'
      : isBool && c.operator === 'equals' ? 'is true'
      : isBool && c.operator === 'not_equals' ? 'is false'
      : c.operator === 'equals' ? `= ${c.value ?? ''}`
      : c.operator === 'not_equals' ? `≠ ${c.value ?? ''}`
      : `${c.operator} ${c.value ?? ''}`;
    return `${label} ${op}`;
  }).join(` ${logic} `);
}

export function conditionGroupSummary(groups: ConditionGroup[], groupLogic: 'AND' | 'OR'): string {
  if (groups.length === 0) return 'catch-all';
  if (groups.length === 1) return conditionSummary(groups[0].conditions, groups[0].logic);
  return groups.map(g => `(${conditionSummary(g.conditions, g.logic)})`).join(` ${groupLogic} `);
}

// ── Condition group normalization ─────────────────────────────────────────────

/** Migrate flat conditions → condition_groups on read. No DB migration needed. */
export function normalizeRowGroups(row: WaterfallRow): WaterfallRow {
  if (row.condition_groups && row.condition_groups.length > 0) return row;
  // Wrap legacy flat conditions into a single group
  if (row.conditions && row.conditions.length > 0) {
    return {
      ...row,
      condition_groups: [{
        id: crypto.randomUUID(),
        conditions: row.conditions,
        logic: row.condition_logic ?? 'AND',
      }],
      group_logic: 'AND',
    };
  }
  return { ...row, condition_groups: [], group_logic: 'AND' };
}

// ── Template → WaterfallRow converter ────────────────────────────────────────

function inferLogic(conditionLogic: string): 'AND' | 'OR' {
  if (typeof conditionLogic === 'string' && conditionLogic.includes('OR')) return 'OR';
  return 'AND';
}

export function templateToRows(
  templateRows: any[],
  statusColumns: string[],
): WaterfallRow[] {
  return templateRows.map(r => {
    const cells: Record<string, CellConfig> = {};
    // r.cells may be an array (seed format) or a Record<string, CellConfig> (saved format)
    const cellsIsArray = Array.isArray(r.cells);
    for (const col of statusColumns) {
      const existing = cellsIsArray
        ? (r.cells ?? []).find((c: any) => (c.status ?? '').toLowerCase() === col.toLowerCase())
        : r.cells?.[col] ?? Object.entries(r.cells ?? {}).find(([k]) => k.toLowerCase() === col.toLowerCase())?.[1];
      // Resolve row-level sender for migration (used as default if cell has no sender)
      const TOKEN_MAP: Record<string, 'gtme_owner_email' | 'gs_owner_email' | 'claydr_email'> = {
        gtme_owner: 'gtme_owner_email', gtme_owner_email: 'gtme_owner_email',
        gs_owner: 'gs_owner_email',     gs_owner_email: 'gs_owner_email',
        claydr: 'claydr_email',         claydr_email: 'claydr_email',
      };
      let rowSender: SenderValue | null = null;
      if (typeof r.sender === 'string') {
        const mapped = TOKEN_MAP[r.sender];
        rowSender = mapped ? { type: 'token', token: mapped } : { type: 'static', email: r.sender };
      } else if (r.sender && typeof r.sender === 'object') {
        rowSender = r.sender as SenderValue;
      }

      cells[col] = {
        enabled: existing?.enabled ?? false,
        channel: existing?.channel ?? 'gong_engage',
        flow_id: existing?.flow_id ?? '',
        has_custom_copy: cellsIsArray ? !!(existing?.custom_copy) : (existing?.has_custom_copy ?? false),
        subject: cellsIsArray ? (existing?.custom_copy?.subject ?? '') : (existing?.subject ?? ''),
        body: cellsIsArray ? (existing?.custom_copy?.body ?? '') : (existing?.body ?? ''),
        copy_status: cellsIsArray ? (existing?.custom_copy ? 'draft' : null) : (existing?.copy_status ?? null),
        // Prefer saved cell-level sender; fall back to row-level sender, then default token
        sender: existing?.sender || rowSender || { type: 'token', token: 'gtme_owner_email' },
        cc: existing?.cc ?? null,
        reply_to: existing?.reply_to ?? null,
        has_claydr_handoff: existing?.has_claydr_handoff ?? false,
        handoff_hours: existing?.handoff_hours ?? 8,
        email_type: existing?.email_type ?? 'manual',
      };
    }

    const conditions = (r.conditions ?? []).map((c: any) => ({
      field: c.field,
      operator: c.operator as ConditionOperator,
      value: c.value !== null && c.value !== undefined ? String(c.value) : null,
    }));
    const conditionLogic = inferLogic(r.condition_logic ?? 'AND');

    // Migrate condition_groups from saved data, or wrap flat conditions
    let conditionGroups: ConditionGroup[] = [];
    let groupLogic: 'AND' | 'OR' = 'AND';
    if (Array.isArray(r.condition_groups) && r.condition_groups.length > 0) {
      conditionGroups = r.condition_groups.map((g: any) => ({
        id: g.id ?? crypto.randomUUID(),
        conditions: (g.conditions ?? []).map((c: any) => ({
          field: c.field,
          operator: c.operator as ConditionOperator,
          value: c.value !== null && c.value !== undefined
            ? (Array.isArray(c.value) ? c.value : String(c.value))
            : null,
        })),
        logic: g.logic === 'OR' ? 'OR' as const : 'AND' as const,
      }));
      groupLogic = r.group_logic === 'OR' ? 'OR' : 'AND';
    } else if (conditions.length > 0) {
      conditionGroups = [{ id: crypto.randomUUID(), conditions, logic: conditionLogic }];
    }

    return {
      id: r.id,
      name: r.name,
      conditions,
      condition_logic: conditionLogic,
      condition_groups: conditionGroups,
      group_logic: groupLogic,
      follow_up_description: r.follow_up_description ?? '',
      sender: null,
      cc: null,
      reply_to: null,
      cells,
    };
  });
}

// ── Waterfall evaluation ──────────────────────────────────────────────────────

export function getMemberField(member: CampaignMember, field: string): unknown {
  return (member as unknown as Record<string, unknown>)[field];
}

export function evalCondition(member: CampaignMember, cond: Condition): boolean {
  const val = getMemberField(member, cond.field);
  const strVal = val !== null && val !== undefined ? String(val).toLowerCase() : '';
  const condVal = Array.isArray(cond.value) ? '' : (cond.value !== null && cond.value !== undefined ? String(cond.value).toLowerCase() : '');
  const now = new Date();
  switch (cond.operator) {
    case 'is_not_null': return val !== null && val !== undefined && val !== '';
    case 'is_null':     return val === null || val === undefined || val === '';
    case 'in_any_of': {
      const list = Array.isArray(cond.value) ? cond.value : [];
      if (list.length === 0) return false;
      return list.some(v => String(val ?? '').toLowerCase() === v.toLowerCase());
    }
    case 'not_in_any_of': {
      const list = Array.isArray(cond.value) ? cond.value : [];
      if (list.length === 0) return true;
      return !list.some(v => String(val ?? '').toLowerCase() === v.toLowerCase());
    }
    case 'equals': {
      const fieldDef = CONDITION_FIELDS.find(f => f.field === cond.field);
      if (fieldDef?.type === 'boolean') {
        // 'equals' on boolean = "is true": val is true or string "true"
        return val === true || strVal === 'true';
      }
      return strVal === condVal;
    }
    case 'not_equals': {
      const fieldDef2 = CONDITION_FIELDS.find(f => f.field === cond.field);
      if (fieldDef2?.type === 'boolean') {
        // 'not_equals' on boolean = "is false": val is not true
        return val !== true && strVal !== 'true';
      }
      return strVal !== condVal;
    }
    case 'contains':    return strVal.includes(condVal);
    case 'not_contains':return !strVal.includes(condVal);
    case 'contains_any': {
      const list = Array.isArray(cond.value) ? cond.value : [];
      if (list.length === 0) return false;
      return list.some(v => strVal.includes(v.toLowerCase()));
    }
    case 'gt':          return Number(val) > Number(cond.value);
    case 'lt':          return Number(val) < Number(cond.value);
    case 'in_last_n_days': {
      const d = new Date(String(val)); if (isNaN(d.getTime())) return false;
      const cut = new Date(); cut.setDate(cut.getDate() - Number(cond.value));
      return d >= cut && d <= now;
    }
    case 'not_in_last_n_days': {
      const d = new Date(String(val)); if (isNaN(d.getTime())) return true;
      const cut = new Date(); cut.setDate(cut.getDate() - Number(cond.value));
      return !(d >= cut && d <= now);
    }
    case 'in_next_n_days': {
      const d = new Date(String(val)); if (isNaN(d.getTime())) return false;
      const cut = new Date(); cut.setDate(cut.getDate() + Number(cond.value));
      return d >= now && d <= cut;
    }
    case 'not_in_next_n_days': {
      const d = new Date(String(val)); if (isNaN(d.getTime())) return true;
      const cut = new Date(); cut.setDate(cut.getDate() + Number(cond.value));
      return !(d >= now && d <= cut);
    }
    default:            return false;
  }
}

export function evalRow(member: CampaignMember, row: WaterfallRow): boolean {
  const groups = row.condition_groups ?? [];
  if (groups.length === 0) {
    // Legacy fallback: use flat conditions
    if (!row.conditions?.length) return true; // catch-all
    const results = row.conditions.map(c => evalCondition(member, c));
    return row.condition_logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  }
  const groupResults = groups.map(g => {
    if (g.conditions.length === 0) return true;
    const results = g.conditions.map(c => evalCondition(member, c));
    return g.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  });
  return (row.group_logic ?? 'AND') === 'OR'
    ? groupResults.some(Boolean)
    : groupResults.every(Boolean);
}

// WillSend = members who will actually receive outreach (matched + cell enabled for their status).
export function calculateWillSend(
  members: CampaignMember[],
  rows: WaterfallRow[],
  statusMapping: StatusMapping,
): Record<string, number> {
  const willSend: Record<string, number> = {};
  for (const row of rows) willSend[row.id] = 0;
  for (const member of members) {
    const rawStatus = member.cm_status ?? '';
    let targetColumn: string | null = null;
    for (const [col, statuses] of Object.entries(statusMapping)) {
      if (statuses.some(s => s.toLowerCase() === rawStatus.toLowerCase())) { targetColumn = col; break; }
    }
    if (!targetColumn) continue;
    const matchedRow = rows.find(r => evalRow(member, r));
    if (!matchedRow) continue;
    if (!matchedRow.cells[targetColumn]?.enabled) continue;
    willSend[matchedRow.id]++;
  }
  return willSend;
}

// Reach = members who first match each row in waterfall order (first-match-wins).
// Unmapped members (cm_status not in statusMapping) are excluded when statusMapping is provided.
export function calculateRowReach(
  members: CampaignMember[],
  rows: WaterfallRow[],
  statusMapping?: StatusMapping,
): Record<string, number> {
  const reach: Record<string, number> = {};
  for (const row of rows) reach[row.id] = 0;
  for (const member of members) {
    if (statusMapping) {
      const rawStatus = member.cm_status ?? '';
      const isMapped = Object.values(statusMapping).some(statuses =>
        statuses.some(s => s.toLowerCase() === rawStatus.toLowerCase())
      );
      if (!isMapped) continue;
    }
    const matched = rows.find(r => evalRow(member, r));
    if (matched) reach[matched.id]++;
  }
  return reach;
}

export type DistributionMap = Record<string, Record<string, number>>;

export function calculateDistribution(
  members: CampaignMember[],
  rows: WaterfallRow[],
  statusMapping: StatusMapping,
): DistributionMap {
  const dist: DistributionMap = {};
  for (const row of rows) {
    dist[row.id] = {};
  }

  for (const member of members) {
    // Find first matching row
    const matchedRow = rows.find(r => evalRow(member, r));
    if (!matchedRow) continue;

    // Resolve raw CM status → parent column
    const rawStatus = member.cm_status ?? '';
    let parentCol: string | null = null;
    for (const [col, statuses] of Object.entries(statusMapping)) {
      if (statuses.some(s => s.toLowerCase() === rawStatus.toLowerCase())) {
        parentCol = col;
        break;
      }
    }
    if (!parentCol) continue;

    const cellConfig = matchedRow.cells[parentCol];
    if (!cellConfig?.enabled) continue;

    dist[matchedRow.id][parentCol] = (dist[matchedRow.id][parentCol] ?? 0) + 1;
  }

  return dist;
}

export interface SenderGap {
  rowId: string;
  rowName: string;
  column: string;
  token: 'gs_owner_email' | 'gtme_owner_email' | 'claydr_email';
  gapType: 'missing' | 'integration_user';
  count: number;
}

const INTEGRATION_PATTERNS = ['integration', 'noreply', 'salesforce', 'sfdc'];

function isIntegrationEmail(email: string | null): boolean {
  if (!email) return false;
  const lower = email.toLowerCase();
  return INTEGRATION_PATTERNS.some(p => lower.includes(p));
}

export function calculateSenderGaps(
  members: CampaignMember[],
  rows: WaterfallRow[],
  statusMapping: StatusMapping,
): SenderGap[] {
  const counts = new Map<string, SenderGap>();

  for (const member of members) {
    const rawStatus = member.cm_status ?? '';
    let targetColumn: string | null = null;
    for (const [col, statuses] of Object.entries(statusMapping)) {
      if (statuses.some(s => s.toLowerCase() === rawStatus.toLowerCase())) { targetColumn = col; break; }
    }
    if (!targetColumn) continue;

    const matchedRow = rows.find(r => evalRow(member, r));
    if (!matchedRow) continue;

    const cell = matchedRow.cells[targetColumn];
    if (!cell?.enabled) continue;

    const sender = cell.sender;
    if (!sender || sender.type !== 'token' || !sender.token) continue;

    const token = sender.token;
    let gapType: 'missing' | 'integration_user' | null = null;

    if (token === 'gs_owner_email' && !member.gs_owner_email) {
      gapType = 'missing';
    } else if (token === 'claydr_email' && !member.claydr_email) {
      gapType = 'missing';
    } else if (token === 'gtme_owner_email') {
      if (isIntegrationEmail(member.gtme_owner_email)) gapType = 'integration_user';
      else if (!member.gtme_owner_email) gapType = 'missing';
    }

    if (!gapType) continue;

    const key = `${matchedRow.id}|${targetColumn}|${token}|${gapType}`;
    const existing = counts.get(key);
    if (existing) {
      existing.count++;
    } else {
      counts.set(key, { rowId: matchedRow.id, rowName: matchedRow.name || 'Unnamed', column: targetColumn, token, gapType, count: 1 });
    }
  }

  return Array.from(counts.values());
}
